import type { FeatureDefinition } from "@growthbook/growthbook-react";

/**
 * GrowthBook feature definitions — git-versioned experiment config.
 *
 * To activate an experiment: set coverage > 0 (0.0–1.0 = % of eligible users).
 * To add a new experiment: add a key, defaultValue, and rules entry.
 * Targeting conditions match GrowthBook attribute names set in ExperimentProvider.
 *
 * Attribute reference (set in src/providers/experiment-provider.tsx):
 *   id, planId, isTrial, isNewUser, userType, isCompanyDomain, onboardingGoal, teamSize,
 *   hasUploaded, trialDaysRemaining, isPaidSubscriber,
 *   magicPromptQueries (cumulative AI Chat prompts; ICP-1 signal),
 *   uploadVolume (total media file count; ICP-2 signal)
 *
 * ICP targeting thresholds:
 *   magicPromptQueries (cumulative AI Chat prompts, all-time):
 *     $gte: 500   — broad signal (regular Chat users)
 *     $gte: 2000  — high-confidence ICP-1 signal
 *     $gte: 10000 — power analyst (ICP-1 core cohort avg: ~18,940)
 *   uploadVolume (mediaList.totalCount — all file types):
 *     $gte: 20  — regular uploader
 *     $gte: 100 — ICP-2 signal (note: includes text notes + recorder submissions;
 *                 inflated vs Intercom sp_audio_video_files which counts audio/video only)
 */
export const FEATURES: Record<string, FeatureDefinition> = {
  /**
   * Booking CTA topbar — persistent "Book a call" button for sales-qualified
   * users (server-computed `salesQualified` profile field mirrored into the
   * GrowthBook attributes by ExperimentProvider).
   * GrowthBook: git-synced | Status: GRADUATED at launch (force: true, 2026-07-31) —
   * operator decision: the button shows to EVERY salesQualified user, no holdout.
   * Booking impact is read from the demo_bookings ledger by surface
   * (utm_content=topbar_book_call) as absolute volume, not an A/B delta.
   * Server prerequisite shipped: salesQualified on /user/profile (speak-server
   * PR #3809, in production). The powerspot resolver suppresses its book_meeting
   * rotation slot for these users so nobody sees two booking CTAs.
   */
  "booking-cta-topbar": {
    defaultValue: false,
    rules: [
      {
        condition: { salesQualified: true },
        force: true,
        key: "booking-cta-topbar",
      },
    ],
  },

  /**
   * Multimodal launch bar — announcement bar above the topbar for the audio +
   * video analysis launch. GRADUATED at launch (force: true) per the no-holdout
   * doctrine: an announcement withheld from a holdout is an announcement that
   * did not happen. Two audiences are separated inside the component, not here:
   * companies with isMultimodalAnalysis get a pointer to the in-chat control,
   * everyone else gets the booking CTA (salesQualified-gated, like every other
   * booking surface). Impact: demo_booked volume, utm_content=launch_bar.
   * Status: OFF since 2026-09-03 (2,910 impressions / 2 clicks in the prior
   * 30 days); its copy arm was also co-assigned to 79% of booking-cta-copy.
   */
  "multimodal-launch-bar": {
    defaultValue: false,
    rules: [
      {
        force: false,
        key: "multimodal-launch-bar",
      },
    ],
  },

  /**
   * Multimodal launch bar incentive — appends the early-access offer line
   * (pilot on the customer's own recordings + a direct line to the team) to
   * whichever copy arm the user is in. A kill switch, not an arm: it rides on
   * top of both arms so switching it off leaves the arm read intact, and a
   * 2x2 against the copy arms would be underpowered at this surface's volume.
   * Read as a before/after step change in demo_booked at utm_content=launch_bar.
   * The offer is service-shaped on purpose — no credits, no trial extension,
   * nothing that needs a fulfilment path or discounts a contract-sized deal.
   */
  "multimodal-launch-bar-incentive": {
    defaultValue: false,
    rules: [
      {
        force: true,
        key: "multimodal-launch-bar-incentive",
      },
    ],
  },

  /**
   * Multimodal in-app promos — the contextual surfaces that follow the launch
   * bar: the locked analysis-mode options in the AI-chat dropdown (grayed +
   * lock, booking upsell on click, utm_content=chat_analysis_locked) and the
   * media-insight promo banners (video: below the player, audio: above the
   * transport controls; utm_content=media_video_promo / media_audio_promo;
   * enabled workspaces get a "Try it in chat" pointer instead of a booking
   * ask). ON at launch (force: true) — a kill switch, not an experiment: the
   * enabled-workspace dropdown replaces the old analysis toggle regardless,
   * only the promo/upsell layer rides this flag. Impact: demo_booked volume by
   * utm_content in the demo_bookings ledger.
   */
  "multimodal-inapp-promos": {
    defaultValue: false,
    rules: [
      {
        force: true,
        key: "multimodal-inapp-promos",
      },
    ],
  },


  /**
   * Exit-intent booking job — consult-offer dialog for sales-qualified users
   * leaving media-insight/dashboard. Gates the highest-priority EXIT_JOBS entry
   * (booking-consult) in pickJob; the legacy jobs are untouched when off.
   * Status: GRADUATED (force: true, 2026-07-31) — live for every salesQualified user
   * per the no-holdout doctrine. Impact: demo_booked volume (ledger, utm_content=exit_intent).
   */
  "exit-intent-booking-job": {
    defaultValue: false,
    rules: [
      {
        condition: { salesQualified: true },
        force: true,
        key: "exit-intent-booking-job",
      },
    ],
  },

  /**
   * Post-transcript consult card — banner on the media-insight surface for
   * sales-qualified users once a file is processed (the aha moment).
   * Status: GRADUATED (force: true, 2026-07-31) — live for every salesQualified user
   * per the no-holdout doctrine. Impact: demo_booked volume (ledger,
   * utm_content=post_transcript_card). 30-day per-user dismissal in localStorage.
   */
  "booking-consult-card": {
    defaultValue: false,
    rules: [
      {
        condition: { salesQualified: true },
        force: true,
        key: "booking-consult-card",
      },
    ],
  },

  /**
   * Booking touchpoints wave 2 — dashboard lifecycle banner (onboarding_complete /
   * activation_complete), AI-chat panel banner (ai_chat), and the locked-feature
   * upsell booking swap (locked_feature). GRADUATED at launch (force: true,
   * 2026-07-31) per the no-holdout doctrine: every surface live for every
   * salesQualified user; per-surface impact read as absolute booking volume by
   * utm_content; copy unified through useBookingCta.
   */
  "booking-touchpoints": {
    defaultValue: false,
    rules: [
      {
        condition: { salesQualified: true },
        force: true,
        key: "booking-touchpoints",
      },
    ],
  },


  /**
   * Exit-intent engine — in-app mouseleave trigger + engagement gate + job picker.
   * GrowthBook: feature exit-intent-engine (git-synced) | Status: graduated (2026-07-10).
   * Rule 1 — uploaded users on trial OR paid (trial-value-demo + feedback jobs). Graduated to 100%
   *   via `force: true`: every eligible uploader gets the modal, no control arm, no bucketing (so it
   *   cannot contaminate the special-offers-holdout population).
   * Rule 2 — active-trial zero-upload users (the zero-upload job). Graduated to 100% via `force: true`,
   *   so every eligible user gets the modal — there is no control arm to compare against.
   *   trialDaysRemaining > 0 excludes expired trials (a win-back motion, not activation). Rules are
   *   mutually exclusive by hasUploaded. Server prerequisite (FeedbackReason enum extension +
   *   optional score) shipped to prod via speak-server #3420 → #3421, so the leave-reason chips
   *   persist instead of 422-ing.
   */
  "exit-intent-engine": {
    defaultValue: false,
    rules: [
      {
        condition: { hasUploaded: true, $or: [{ isTrial: true }, { isPaidSubscriber: true }] },
        force: true,
        key: "exit-intent-engine",
      },
      {
        condition: { hasUploaded: false, isTrial: true, trialDaysRemaining: { $gt: 0 } },
        force: true,
        key: "exit-intent-engine-zero-upload",
      },
    ],
  },





  "sidebar-customization": {
    defaultValue: false,
    rules: [
      {
        force: true,
        key: "sidebar-customization",
      },
    ],
  },

  /** GrowthBook: exp_19g6dmvjwhesy | Status: DRAFT | Winner metric: Subscription Started (reliable); teammate joins within 7 days needs a server metric | Excludes: none */
  "trial-team-invite": {
    defaultValue: "control",
    rules: [
      {
        condition: { isTrial: true, trialDaysRemaining: { $gt: 0 } },
        variations: ["control", "show_bar"],
        weights: [0.5, 0.5],
        key: "trial-team-invite",
        coverage: 0,
      },
    ],
  },

  /**
   * Cancel-flow retention A/B — CTA hierarchy test for paid subscribers entering cancel flow.
   * GrowthBook: (assign ID after activating in GrowthBook dashboard) | Status: DRAFT, coverage: 0.
   * Targeting: isPaidSubscriber: true (wired in ExperimentProvider).
   * Do NOT set coverage > 0 until cancel-flow UI branching is implemented and approved.
   * Metric: retained_outcome_rate (subscription/retain funnel events in Sentry Discover).
   */
  "cancel-flow-retention": {
    defaultValue: false,
    rules: [
      {
        condition: { isPaidSubscriber: true },
        variations: [false, true],
        weights: [0.5, 0.5],
        key: "cancel-flow-retention",
        coverage: 0,
      },
    ],
  },

  /**
   * Pricing base-plan price test — monthly subscription price point ($15 / $19 / $25).
   * GrowthBook: (assign ID after creating in GrowthBook dashboard) | Status: DRAFT, coverage: 0.
   * Dormant shell — flip coverage > 0 from the GrowthBook dashboard when the live pricing
   * experiment activates. defaultValue "base_15" is the current live monthly price; cookie-blocked
   * users fall through to defaultValue and must never land on a higher-priced arm.
   * hashAttribute "gb_client_id" buckets on the cross-domain ID so a visitor who saw a price on
   * speakai.co keeps the same price after sign-up (matches the WP plugin's bucketing key).
   */
  "pricing-base-plan": {
    defaultValue: "base_15",
    rules: [
      {
        variations: ["base_15", "base_19", "base_25"],
        weights: [0.34, 0.33, 0.33],
        key: "pricing-base-plan",
        coverage: 0,
        hashAttribute: "gb_client_id",
      },
    ],
  },

  /**
   * Pricing annual-discount test — annual-plan discount depth (10% / 20% / 30%).
   * GrowthBook: (assign ID after creating in GrowthBook dashboard) | Status: DRAFT, coverage: 0.
   * Dormant shell — flip coverage > 0 from the GrowthBook dashboard. defaultValue "annual_20" is
   * the current live annual discount (20%, per src/features/pricing/pricing-data.ts); cookie-blocked
   * users fall through to it.
   * hashAttribute "gb_client_id" — same cross-domain bucketing key as the other pricing shells.
   */
  "pricing-annual-discount": {
    defaultValue: "annual_20",
    rules: [
      {
        variations: ["annual_10", "annual_20", "annual_30"],
        weights: [0.34, 0.33, 0.33],
        key: "pricing-annual-discount",
        coverage: 0,
        hashAttribute: "gb_client_id",
      },
    ],
  },


  /**
   * Onboarding auto-setup — runs the full createOnboardingWorkspace path
   * (goal-named folder + goal-mapped fields + run-once automation + folder view)
   * instead of the legacy single createFirstAutomation.
   * Status: PROMOTED 2026-08-23 to 100% as the default onboarding flow. The
   * 2026-07-31 experiment read neutral against the generic 3-field treatment
   * (n=6,938); the three-bucket redesign replaced that treatment with
   * bucket-personalized workspaces (8-field sales config, fields-tab-first
   * insight view), so the workspace path now ships to everyone. The server
   * still branches on the variant in POST /user/onboarding/complete and keeps
   * the legacy path as its failure fallback.
   */
  "onboarding-auto-setup": {
    defaultValue: "auto_setup",
    rules: [],
  },




  /**
   * Special-offers holdout — measure incremental revenue from showing special offers.
   * 50% see their eligible offer, 50% holdout (offer + pill suppressed).
   * Eligible = non-paid (isPaidSubscriber:false) AND server returned an offer AND canManageBilling
   * (the offer-presence + billing gates are enforced client-side in the consuming components).
   * defaultValue "show" — cookie-blocked / unresolved users always see the offer.
   * Holdout applies to the in-app pill + /special-offers page only, NOT the public /claim-offer link.
   * Rule key "special-offers-holdout-v3" (v2 ran against suites that expired 2026-09-09, so it has no
   * usable read); v3 starts with the credit-denominated Calls/Research/Meeting suites relaunched
   * 2026-09-30. Primary metric: GA4 Subscription/PurchaseSuccess with source=special_offer.
   */
  "special-offers-holdout": {
    defaultValue: "show",
    rules: [
      {
        condition: { isPaidSubscriber: false },
        variations: ["show", "holdout"],
        weights: [0.5, 0.5],
        key: "special-offers-holdout-v3",
        coverage: 1,
      },
    ],
  },

  /**
   * Paywall at intent — when a trial or pay-as-you-go user is blocked for credits (AI chat, upload,
   * media, translation, meeting bot) or hits the expired-account gate, show Pro monthly + a $20
   * top-up in place ("keep_going") instead of the full pricing page / top-up-only dialog ("control").
   * defaultValue "control" — cookie-blocked / unresolved users keep today's behaviour.
   * Primary metric: blocked companies paying within 7 days; guardrail: first payers per week.
   * RUNNING since 2026-10-21: every not-paid user (trial, expired, pay-as-you-go) at a block, split 50/50.
   */
  "paywall-at-intent": {
    defaultValue: "control",
    rules: [
      {
        condition: { isPaidSubscriber: false },
        variations: ["control", "keep_going"],
        weights: [0.5, 0.5],
        key: "paywall-at-intent",
        coverage: 1,
      },
    ],
  },

  /** Special-offer pill copy A/B/C/D — CONCLUDED 2026-09-29, coverage 0: last read (2026-09-09) had arm B leading Tier A directionally, 26.6% vs 20-22%; not part of the v3 relaunch. */
  "special_offer_pill_copy": {
    defaultValue: "A",
    rules: [
      {
        variations: ["A", "B", "C", "D"],
        weights: [0.25, 0.25, 0.25, 0.25],
        key: "special_offer_pill_copy",
        coverage: 0,
      },
    ],
  },

  /**
   * Session-moment beacon — Intercom events (`moment_presence` / `moment_route`
   * / `moment_player`) for accounts younger than 7 days, powering the
   * proactive-outreach engine's live-now and playback-suppression signals
   * (growth-engine modules/support/intercom/proactive.py).
   * GrowthBook: git-synced | Status: ON at launch (force: true, 2026-08-07) —
   * a telemetry rail, not an A/B test; this flag is the remote kill switch.
   */
  "moment-beacon": {
    defaultValue: false,
    rules: [
      {
        force: true,
        key: "moment-beacon",
      },
    ],
  },
};
